package org.example.project.enginepersonal

import org.example.project.settings.UISettings

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.coroutines.launch
import kotlinx.serialization.Serializable
import kotlinx.serialization.builtins.ListSerializer
import kotlinx.serialization.builtins.serializer
import kotlinx.serialization.json.Json
import org.example.project.dashboard.LocalScreenRefreshVersion
import org.example.project.enginekartei.KarteiApi
import org.example.project.manager.LumaticaApiManager
import org.example.project.service.GlobalManager
import org.example.project.service.LoginService
import org.example.project.settings.AppDropdown
import org.example.project.settings.AppDropdownAppearance
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.settings.LumaticaCheckbox
import org.example.project.utils.All
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Style
import org.jetbrains.compose.web.dom.Text
import kotlin.js.Date

private val stempeluhrProfilJson = Json {
    ignoreUnknownKeys = true
    encodeDefaults = false
}

@Serializable
data class StempeluhrProfil(
    val id: String = "",
    val label: String = "",
    val standortId: String = "",
    val standort: String = "",
    val standortAnzeige: String = "",
    val abteilung: String = "",
    val abteilungen: List<String> = emptyList(),
    val direktWechsel: Boolean = false,
    val aktiv: Boolean = true
)

@Serializable
data class StempeluhrProfilResponse(
    val ok: Boolean = false,
    val error: String = "",
    val token: String = "",
    val setupToken: String = "",
    val terminals: List<StempeluhrProfil> = emptyList(),
    val terminal: StempeluhrProfil = StempeluhrProfil()
)

internal object StempeluhrProfilApi {
    private fun firmenId(): String =
        LoginService.getUser()?.firmenId.orEmpty().trim()
            .ifBlank { GlobalManager.currentFirmenId.trim() }

    private fun apiPart(value: String): String =
        js("encodeURIComponent")(if (value.isBlank()) "~" else value) as String

    suspend fun loadAll(): List<StempeluhrProfil> {
        val raw = runCatching {
            LumaticaApiManager.call("zeiterfassung/terminal-list-json/${apiPart(firmenId())}")
        }.getOrDefault("")
        return runCatching {
            stempeluhrProfilJson.decodeFromString(ListSerializer(StempeluhrProfil.serializer()), raw)
        }.getOrDefault(emptyList())
    }

    suspend fun save(profile: StempeluhrProfil): StempeluhrProfilResponse {
        val raw = runCatching {
            LumaticaApiManager.postJson(
                "zeiterfassung/terminal-save-json/${apiPart(firmenId())}",
                stempeluhrProfilJson.encodeToString(StempeluhrProfil.serializer(), profile)
            )
        }.getOrDefault("")
        return decodeResponse(raw).also { result ->
            if (result.ok) {
                val id = result.terminal.id.ifBlank { profile.id }
                GlobalManager.markJsonChanged("zeit", "stempeluhren/$id/stempeluhr.json")
            }
        }
    }

    suspend fun delete(id: String): StempeluhrProfilResponse {
        val body = """{"id":${jsonString(id)}}"""
        val raw = runCatching {
            LumaticaApiManager.postJson(
                "zeiterfassung/terminal-delete-json/${apiPart(firmenId())}",
                body
            )
        }.getOrDefault("")
        return decodeResponse(raw).also { result ->
            if (result.ok) {
                GlobalManager.markJsonChanged("zeit", "stempeluhren/$id/stempeluhr.json")
            }
        }
    }

    suspend fun saveGlobalPin(pin: String): StempeluhrProfilResponse {
        val body = """{"pin":${jsonString(pin)}}"""
        val raw = runCatching {
            LumaticaApiManager.postJson(
                "zeiterfassung/terminal-pin-save-json/${apiPart(firmenId())}",
                body
            )
        }.getOrDefault("")
        return decodeResponse(raw).also { result ->
            if (result.ok) {
                GlobalManager.markJsonChanged("zeit", "stempeluhren/settings.json")
            }
        }
    }

    suspend fun unlock(pin: String): StempeluhrProfilResponse {
        val body = """{"pin":${jsonString(pin)}}"""
        val raw = runCatching {
            LumaticaApiManager.postJson(
                "zeiterfassung/terminal-unlock-json/${apiPart(firmenId())}",
                body
            )
        }.getOrDefault("")
        return decodeResponse(raw)
    }

    suspend fun activate(id: String, setupToken: String): StempeluhrProfilResponse {
        val body = """{"id":${jsonString(id)},"setupToken":${jsonString(setupToken)}}"""
        val raw = runCatching {
            LumaticaApiManager.postJson(
                "zeiterfassung/terminal-activate-json/${apiPart(firmenId())}",
                body
            )
        }.getOrDefault("")
        return decodeResponse(raw)
    }

    suspend fun loadActivated(token: String): StempeluhrProfilResponse {
        val body = """{"token":${jsonString(token)}}"""
        val raw = runCatching {
            LumaticaApiManager.postJson(
                "zeiterfassung/terminal-profile-json/${apiPart(firmenId())}",
                body
            )
        }.getOrDefault("")
        return decodeResponse(raw)
    }

    private fun decodeResponse(raw: String): StempeluhrProfilResponse =
        runCatching {
            stempeluhrProfilJson.decodeFromString(StempeluhrProfilResponse.serializer(), raw)
        }.getOrDefault(StempeluhrProfilResponse(error = "Stempeluhr-Profil konnte nicht geladen werden."))

    private fun jsonString(value: String): String =
        stempeluhrProfilJson.encodeToString(String.serializer(), value)
}

@Composable
fun StempeluhrGeneratorScreen() {
    val scope = rememberCoroutineScope()
    val refreshVersion = maxOf(
        LocalScreenRefreshVersion.current,
        GlobalManager.jsonDomainVersion("zeit"),
        GlobalManager.jsonDomainVersion("personal"),
        GlobalManager.jsonDomainVersion("standort")
    )
    var profiles by remember { mutableStateOf<List<StempeluhrProfil>>(emptyList()) }
    var locations by remember { mutableStateOf<List<All>>(emptyList()) }
    var selectedId by remember { mutableStateOf("") }
    var draft by remember { mutableStateOf(StempeluhrProfil()) }
    var loading by remember { mutableStateOf(true) }
    var saving by remember { mutableStateOf(false) }
    var message by remember { mutableStateOf("") }
    var messageError by remember { mutableStateOf(false) }
    var globalPin by remember { mutableStateOf("") }
    var showPinSettings by remember { mutableStateOf(false) }

    suspend fun reload() {
        loading = true
        profiles = StempeluhrProfilApi.loadAll()
        val activeLocations = PersonalakteApi.loadActiveLocations()
        locations = activeLocations.ifEmpty {
            KarteiApi.loadRegisterFirma()
                ?.let(::stempeluhrFirmaLocationFallback)
                ?.let(::listOf)
                .orEmpty()
        }
        if (selectedId.isBlank() || profiles.none { it.id == selectedId }) {
            selectedId = profiles.firstOrNull()?.id.orEmpty()
        }
        loading = false
    }

    LaunchedEffect(refreshVersion) { reload() }
    LaunchedEffect(selectedId, profiles) {
        profiles.firstOrNull { it.id == selectedId }?.let { draft = it }
    }

    val selectedLocation = locations.firstOrNull {
        it.id == draft.standortId || it.standort == draft.standort
    }
    val departmentOptions = selectedLocation?.let(::stempeluhrDepartments).orEmpty()

    Style(StempeluhrGeneratorStyles)
    Div({ classes(StempeluhrGeneratorStyles.page) }) {
        Div({ classes(StempeluhrGeneratorStyles.toolbar) }) {
            Div({ classes(StempeluhrGeneratorStyles.titleRow) }) {
                H3({ classes(StempeluhrGeneratorStyles.title) }) { Text("Stempeluhr Generator") }
                Button(attrs = {
                    classes(StempeluhrGeneratorStyles.iconButton)
                    attr("title", "Secret-PIN einstellen")
                    onClick {
                        globalPin = ""
                        message = ""
                        messageError = false
                        showPinSettings = true
                    }
                }) {
                    Img(src = "images/settings.svg", attrs = { classes(StempeluhrGeneratorStyles.icon) })
                }
            }
            Div({ classes(StempeluhrGeneratorStyles.profileTabs) }) {
                profiles.forEach { profile ->
                    LumaticaButton(
                        label = profile.label.ifBlank { profile.id },
                        onClick = { selectedId = profile.id },
                        variant = LumaticaButtonVariant.State,
                        isActive = selectedId == profile.id,
                        heightPx = 32
                    )
                }
                LumaticaButton(
                    label = "+ Stempeluhr",
                    onClick = {
                        val id = "uhr-${Date().getTime().toLong()}"
                        selectedId = id
                        draft = StempeluhrProfil(id = id, label = "Neue Stempeluhr")
                        message = ""
                        messageError = false
                    },
                    heightPx = 32
                )
            }
        }

        if (loading) {
            Span { Text("Stempeluhren werden geladen...") }
        } else {
            Div({ classes(StempeluhrGeneratorStyles.card) }) {
                GeneratorInput("Stempeluhr-ID", draft.id, "z. B. lager-1") {
                    if (profiles.none { profile -> profile.id == selectedId }) draft = draft.copy(id = it)
                }
                GeneratorInput("Bezeichnung", draft.label, "z. B. Lager Eingang") {
                    draft = draft.copy(label = it)
                }
                Div({ classes(StempeluhrGeneratorStyles.field) }) {
                    Span({ classes(StempeluhrGeneratorStyles.label) }) { Text("Standort zuweisen") }
                    AppDropdown(
                        options = locations.map {
                            All(key = it.id, label = it.standort.ifBlank { it.bezeichnung.ifBlank { it.id } })
                        },
                        selectedValue = draft.standortId.ifBlank { null },
                        onChange = { locationId ->
                            val location = locations.firstOrNull { it.id == locationId }
                            val departments = location?.let(::stempeluhrDepartments).orEmpty()
                            draft = draft.copy(
                                standortId = locationId,
                                standort = location?.standort.orEmpty().ifBlank { location?.bezeichnung.orEmpty() },
                                standortAnzeige = location?.let(::stempeluhrLocationDisplay).orEmpty(),
                                abteilung = departments.firstOrNull().orEmpty(),
                                abteilungen = departments
                            )
                        },
                        placeholder = "Standort wählen",
                        appearance = AppDropdownAppearance.Surface
                    )
                }
                Div({ classes(StempeluhrGeneratorStyles.field) }) {
                    Span({ classes(StempeluhrGeneratorStyles.label) }) { Text("Abteilung zuweisen") }
                    AppDropdown(
                        options = departmentOptions.map { All(key = it, label = it) },
                        selectedValue = draft.abteilung.ifBlank { null },
                        onChange = { draft = draft.copy(abteilung = it, abteilungen = departmentOptions) },
                        placeholder = "Abteilung wählen",
                        appearance = AppDropdownAppearance.Surface
                    )
                }
                GeneratorToggle("Abteilung direkt wechseln", draft.direktWechsel) {
                    draft = draft.copy(direktWechsel = it)
                }
                GeneratorToggle("Stempeluhr aktiv", draft.aktiv) {
                    draft = draft.copy(aktiv = it)
                }
                Div({ classes(StempeluhrGeneratorStyles.actions) }) {
                    LumaticaButton(
                        label = "Löschen",
                        onClick = {
                            if (saving || profiles.none { it.id == selectedId }) return@LumaticaButton
                            saving = true
                            message = ""
                            messageError = false
                            scope.launch {
                                val result = StempeluhrProfilApi.delete(selectedId)
                                if (result.ok) {
                                    message = "Stempeluhr gelöscht."
                                    messageError = false
                                    selectedId = ""
                                    draft = StempeluhrProfil()
                                    reload()
                                } else {
                                    message = stempeluhrProfileError(result.error)
                                    messageError = true
                                }
                                saving = false
                            }
                        },
                        enabled = profiles.any { it.id == selectedId } && !saving,
                        heightPx = 36
                    )
                    LumaticaButton(
                        label = if (saving) "Speichern..." else "Speichern",
                        onClick = {
                            if (saving) return@LumaticaButton
                            saving = true
                            message = ""
                            messageError = false
                            scope.launch {
                                val result = StempeluhrProfilApi.save(
                                    draft.copy(
                                        standortAnzeige = selectedLocation
                                            ?.let(::stempeluhrLocationDisplay)
                                            .orEmpty()
                                            .ifBlank { draft.standortAnzeige.ifBlank { draft.standort } },
                                        abteilungen = departmentOptions
                                    )
                                )
                                if (result.ok) {
                                    message = "Stempeluhr gespeichert."
                                    messageError = false
                                    selectedId = result.terminal.id
                                    reload()
                                } else {
                                    message = stempeluhrProfileError(result.error)
                                    messageError = true
                                }
                                saving = false
                            }
                        },
                        variant = LumaticaButtonVariant.Action,
                        enabled = draft.id.isNotBlank() && !saving,
                        heightPx = 36
                    )
                }
                if (message.isNotBlank()) {
                    Span({
                        classes(
                            if (messageError) {
                                StempeluhrGeneratorStyles.errorMessage
                            } else {
                                StempeluhrGeneratorStyles.message
                            }
                        )
                    }) { Text(message) }
                }
            }
        }
        if (showPinSettings) {
            Div({ classes(StempeluhrGeneratorStyles.settingsOverlay) }) {
                Div({ classes(StempeluhrGeneratorStyles.settingsBox) }) {
                    H3({ classes(StempeluhrGeneratorStyles.settingsTitle) }) { Text("Secret-PIN") }
                    GeneratorInput(
                        label = "Stempeluhr Secret-PIN",
                        value = globalPin,
                        placeholder = "Mindestens 4 Zeichen",
                        inputType = InputType.Password
                    ) { globalPin = it }
                    if (message.isNotBlank()) {
                        Span({
                            classes(
                                if (messageError) {
                                    StempeluhrGeneratorStyles.errorMessage
                                } else {
                                    StempeluhrGeneratorStyles.message
                                }
                            )
                        }) { Text(message) }
                    }
                    Div({ classes(StempeluhrGeneratorStyles.settingsActions) }) {
                        LumaticaButton(
                            label = "Abbrechen",
                            onClick = {
                                if (saving) return@LumaticaButton
                                globalPin = ""
                                message = ""
                                messageError = false
                                showPinSettings = false
                            },
                            enabled = !saving,
                            heightPx = 36
                        )
                        LumaticaButton(
                            label = "PIN speichern",
                            onClick = {
                                if (saving) return@LumaticaButton
                                saving = true
                                message = ""
                                scope.launch {
                                    val result = StempeluhrProfilApi.saveGlobalPin(globalPin)
                                    messageError = !result.ok
                                    message = if (result.ok) {
                                        globalPin = ""
                                        showPinSettings = false
                                        "Stempeluhr Secret-PIN gespeichert."
                                    } else {
                                        stempeluhrProfileError(result.error)
                                    }
                                    saving = false
                                }
                            },
                            variant = LumaticaButtonVariant.Action,
                            enabled = globalPin.length >= 4 && !saving,
                            heightPx = 36
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun GeneratorInput(
    label: String,
    value: String,
    placeholder: String,
    inputType: InputType<String> = InputType.Text,
    onChange: (String) -> Unit
) {
    Div({ classes(StempeluhrGeneratorStyles.field) }) {
        Span({ classes(StempeluhrGeneratorStyles.label) }) { Text(label) }
        Input(inputType, attrs = {
            classes(StempeluhrGeneratorStyles.input)
            value(value)
            placeholder(placeholder)
            onInput { onChange(it.value) }
        })
    }
}

@Composable
private fun GeneratorToggle(label: String, checked: Boolean, onChange: (Boolean) -> Unit) {
    Div({ classes(StempeluhrGeneratorStyles.toggle) }) {
        LumaticaCheckbox(checked = checked, onChange = onChange, fontSizePx = 18)
        Span { Text(label) }
    }
}

private fun stempeluhrDepartments(location: All): List<String> =
    (
        location.standortLabels.mapNotNull { row ->
            row["text"] ?: row["label"] ?: row["name"]
        } +
            location.abteilungen.map {
                it.bezeichnung.ifBlank { it.name.ifBlank { it.label.ifBlank { it.id } } }
            }
        )
        .map { it.trim() }
        .filter { it.isNotBlank() }
        .distinct()

private fun stempeluhrLocationDisplay(location: All): String {
    val name = location.standort.ifBlank {
        location.bezeichnung.ifBlank { location.firma.ifBlank { location.name } }
    }
    val address = listOf(location.strasse, location.hausnummer)
        .filter { it.isNotBlank() }
        .joinToString(" ")
    return listOf(name, address).filter { it.isNotBlank() }.joinToString(" ")
}

private fun stempeluhrFirmaLocationFallback(firma: All): All {
    val name = firma.firma.ifBlank {
        firma.kunde.ifBlank { firma.name.ifBlank { firma.bezeichnung } }
    }
    val display = listOf(
        name,
        listOf(firma.strasse, firma.hausnummer)
            .filter { it.isNotBlank() }
            .joinToString(" ")
    )
        .filter { it.isNotBlank() }
        .joinToString(" ")
    return All(
        id = "firma-json",
        standort = name,
        bezeichnung = display.ifBlank { name },
        firma = name,
        strasse = firma.strasse,
        hausnummer = firma.hausnummer
    )
}

internal fun stempeluhrProfileError(error: String): String = when (error) {
    "stempeluhr_id_fehlt" -> "Stempeluhr-ID fehlt."
    "stempeluhr_pin_zu_kurz" -> "Die PIN muss mindestens 4 Zeichen haben."
    "stempeluhr_nicht_gefunden" -> "Stempeluhr wurde nicht gefunden oder ist deaktiviert."
    "stempeluhr_pin_falsch" -> "PIN ist falsch."
    "stempeluhr_freigabe_abgelaufen" -> "Freigabe ist abgelaufen. PIN erneut eingeben."
    "stempeluhr_nicht_aktiviert" -> "Stempeluhr ist nicht aktiviert."
    "zeiterfassung_action_unknown" -> "Die Stempeluhr-API ist noch nicht aktiv. Backend neu starten."
    else -> error.ifBlank { "Aktion fehlgeschlagen." }
}

private object StempeluhrGeneratorStyles : StyleSheet() {
    val page by style {
        width(100.percent)
        height(100.percent)
        minHeight(0.px)
        padding(18.px)
        boxSizing("border-box")
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        alignItems(AlignItems.Center)
        gap(14.px)
        overflow("auto")
        color(Color.white)
    }
    val toolbar by style {
        width(100.percent)
        maxWidth(760.px)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(10.px)
    }
    val titleRow by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.SpaceBetween)
        gap(12.px)
        width(100.percent)
    }
    val title by style {
        margin(0.px)
        fontSize(20.px)
        fontWeight("900")
    }
    val iconButton by style {
        width(34.px)
        height(34.px)
        padding(0.px)
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.14))
        borderRadius(7.px)
        backgroundColor(rgba(255, 255, 255, 0.04))
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        cursor("pointer")
        self + hover style {
            backgroundColor(rgba(255, 255, 255, 0.10))
        }
    }
    val icon by style {
        width(18.px)
        height(18.px)
        property("filter", "brightness(0) invert(1)")
        property("pointer-events", "none")
    }
    val profileTabs by style {
        display(DisplayStyle.Flex)
        gap(6.px)
        flexWrap(FlexWrap.Wrap)
    }
    val card by style {
        width(100.percent)
        maxWidth(760.px)
        padding(18.px)
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.12))
        borderRadius(8.px)
        backgroundColor(rgba(255, 255, 255, 0.035))
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(12.px)
        boxSizing("border-box")
    }
    val field by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(5.px)
    }
    val settingsOverlay by style {
        position(Position.Fixed)
        left(0.px)
        top(0.px)
        right(0.px)
        bottom(0.px)
        property("z-index", "100000")
        backgroundColor(rgba(0, 0, 0, 0.72))
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        padding(18.px)
        boxSizing("border-box")
    }
    val settingsBox by style {
        width(420.px)
        maxWidth(100.percent)
        padding(18.px)
        borderRadius(8.px)
        backgroundColor(Color("#17191d"))
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.16))
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(12.px)
        boxSizing("border-box")
    }
    val settingsTitle by style {
        margin(0.px)
        color(Color.white)
        fontSize(18.px)
        fontWeight("900")
    }
    val settingsActions by style {
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.FlexEnd)
        gap(8.px)
    }
    val label by style {
        color(rgba(255, 255, 255, 0.58))
        fontSize(11.px)
        fontWeight("800")
    }
    val input by style {
        width(100.percent)
        height(38.px)
        padding(0.px, 10.px)
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.14))
        borderRadius(6.px)
        backgroundColor(rgba(0, 0, 0, 0.22))
        color(Color.white)
        boxSizing("border-box")
    }
    val toggle by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(8.px)
        fontSize(13.px)
        fontWeight("800")
    }
    val actions by style {
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.FlexEnd)
    }
    val message by style {
        color(Color("#84e1a7"))
        fontSize(12.px)
        fontWeight("800")
    }
    val errorMessage by style {
        color(UISettings.ACCENT)
        fontSize(12.px)
        fontWeight("800")
    }
}
